// The toast shown after a receipt saves.
//
// Two independent things can need reporting, and a save can hit either, both, or
// neither:
//
//   1. LINE delivery — long-standing behaviour, unchanged.
//   2. A receipt-level discount the operator picked that did NOT come off.
//      The server used to drop that silently (receipt_service/createReceipt.js
//      simply skipped it), so the first anyone heard of it was the patient
//      querying their bill. It now returns `warnings: [{ code, message }]`.
//
// A voucher CODE in the same position never reaches here — the server rejects
// that save outright so the single-use card is not burnt — so everything in
// `warnings` is a manual discount and the receipt genuinely did save.
//
// Extracted from ReceiptPreview.vue's success handler so the composition is
// testable without mounting the modal (see tests/unit/receiptSaveFeedback.spec.ts).
// Relative imports only in this directory's utils — the logic specs run under
// plain Playwright/Node with no webpack, so "@/..." does not resolve there.

const LINE_MESSAGES = {
  SENT: "บันทึกข้อมูลใบเสร็จสำเร็จ และส่งข้อความผ่าน LINE สำเร็จ",
  FAILED: "บันทึกข้อมูลใบเสร็จสำเร็จ แต่ไม่สามารถส่งข้อความผ่าน LINE ได้",
};

const BASE_MESSAGE = "บันทึกข้อมูลใบเสร็จสำเร็จ";

/**
 * @param {{isSentLineMessage?: string, warnings?: Array<{code?: string, message?: string}>}} data
 *   the createReceipt response body. A backend without this change sends no
 *   `warnings` at all, which must read exactly as it does today.
 * @returns {{message: string, variant: "success"|"warning"}}
 */
export function receiptSaveFeedback(data) {
  const body = data || {};
  const lineStatus = body.isSentLineMessage;

  let message = LINE_MESSAGES[lineStatus] || BASE_MESSAGE;
  let variant = lineStatus === "FAILED" ? "warning" : "success";

  // Defensive on every step: `warnings` may be absent (older backend), null, or
  // carry an entry with no message. A blank or "undefined" clause appended to a
  // success toast is worse than saying nothing.
  const reasons = (Array.isArray(body.warnings) ? body.warnings : [])
    .map((w) => (w && w.message ? String(w.message).trim() : ""))
    .filter(Boolean);

  if (reasons.length > 0) {
    variant = "warning";
    message = `${message} — ${reasons.join(" ")}`;
  }

  return { message, variant };
}
